// Overview — the only view that isn't a table, and it still behaves like one: a tab
// in the same strip, the same view bar, the same measure. It answers three questions
// in the order you'd ask them: where does the money stand, what is owed next, what
// just happened.
//
// **It was removed once, and the reason it is back is the reason it was removed.**
// The charge was that it showed numbers you could get from the lists it linked to,
// which was true of the numbers — and landing on Clients instead answered a question
// nobody opens the app with. A list of forty clients is how you FIND something. It
// never says what is owed this week or what an agent left waiting. So the rule for
// what earns a place here is narrow: it has to be a thing no single list can show,
// because it spans them.
//
// An earlier version led with a ranked list of things that had gone quiet. It was
// accurate and it was a nag: opening your CRM to a scoreboard of your own neglect
// makes you avoid opening your CRM. Staleness is still on the rows where it's a fact
// about that record; it isn't the front page. The staging line below is not that: a
// suggestion waiting is somebody else's move, not a reproach.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { EmptyState, Section, Stats } from '../components/common'
import { EntryRow } from '../components/EntryThread'
import { Icon } from '../components/Icon'
import { View } from '../components/Page'
import { api } from '../lib/api'
import { money } from '../lib/format'
import { daysSince, rollup } from '../lib/metrics'
import {
  allEntries,
  entitiesOfKind,
  owedItems,
  titleOf,
  stageOf,
  type EntityRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'

export function Overview() {
  const { state, config, version } = useStore()

  const data = useMemo(() => {
    return {
      roll: rollup(state, config),
      recent: allEntries(state).slice(0, 8),
      owed: owedItems(state),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, config])

  const nothing =
    entitiesOfKind(state, 'deal').length === 0 &&
    entitiesOfKind(state, 'project').length === 0 &&
    entitiesOfKind(state, 'org').length === 0

  if (nothing) {
    return (
      <View>
        <EmptyState
          icon="LayoutDashboard"
          title="Nothing here yet"
          hint="Start with a client. Everything else in crum hangs off one."
        />
      </View>
    )
  }

  return (
    <View>
      <Waiting />
      <Stats
        items={[
          { label: 'Pipeline', value: money(data.roll.open, '·'), hue: '--ember' },
          { label: 'Signed', value: money(data.roll.committed, '·'), hue: '--good' },
          { label: 'Delivered', value: money(data.roll.delivered, '·'), hue: '--marine' },
          // The honest "active": work a signed deal is currently paying for.
          { label: 'Live work', value: String(data.roll.liveWork), hue: '--good' },
          { label: 'Owed', value: String(data.owed.length), hue: '--warn' },
        ]}
      />

      {data.owed.length > 0 && (
        <Section
          title="Owed next"
          note="dated deliverables, across every deal"
          right={<GoTo to="/due" label="Everything dated" />}
        >
          <div className="rows">
            {data.owed.slice(0, 6).map((c) => (
              <OwedRow key={c.id} due={c.due} label={c.label} deal={c.deal} />
            ))}
          </div>
        </Section>
      )}

      <Section title="Lately" note="entries across everything">
        {data.recent.length === 0 ? (
          <p className="text-body py-4" style={{ color: 'var(--faint)' }}>
            Nothing logged yet.
          </p>
        ) : (
          <div className="rows">
            {data.recent.map((e) => (
              <EntryRow key={e.id} entry={e} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Pipeline" note="by stage" right={<GoTo to="/deals" label="Deals" />}>
        <StageBars />
      </Section>
    </View>
  )
}

/**
 * What an agent has left for a person to decide.
 *
 * The one thing on this page that is a call to action, and it is here because it is
 * the one thing that is invisible everywhere else: a suggestion sits in the staging
 * table, not in the log, so no list and no record page can show it. Absent when the
 * queue is empty, which is most days — a tab badge that is always there stops being
 * read.
 */
function Waiting() {
  const [count, setCount] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    let live = true
    api
      .suggestions('pending')
      .then((r) => live && setCount(r.suggestions.length))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  if (count === 0) return null
  return (
    <button
      type="button"
      onClick={() => navigate('/staging')}
      className="rounded-control text-body mb-4 flex w-full items-center gap-2.5 border px-3 py-2 text-left"
      style={{
        borderColor: 'color-mix(in srgb, var(--warn) 35%, transparent)',
        background: 'color-mix(in srgb, var(--warn) 8%, transparent)',
      }}
    >
      <Icon name="Archive" size={14} style={{ color: 'var(--warn)' }} />
      <span className="min-w-0 flex-1">
        {count} {count === 1 ? 'suggestion is' : 'suggestions are'} waiting for a decision
      </span>
      <Icon name="ArrowRight" size={14} style={{ color: 'var(--muted)' }} />
    </button>
  )
}

/** One thing owed: when, what, and which deal owes it. */
function OwedRow({ due, label, deal }: { due: string; label: string; deal: string }) {
  const { state } = useStore()
  const navigate = useNavigate()
  const overdue = daysSince(due) > 0

  return (
    <button
      type="button"
      onClick={() => navigate(`/e/${deal}`)}
      className="flex w-full items-center gap-3 py-2 text-left"
    >
      <span
        className="num w-[86px] shrink-0"
        style={{ fontSize: 'var(--text-label)', color: overdue ? 'var(--crit)' : 'var(--warn)' }}
        title={overdue ? `${daysSince(due)} days past` : 'upcoming'}
      >
        {due}
      </span>
      <span className="text-body min-w-0 flex-1 truncate">{label}</span>
      <span className="meta shrink-0 truncate" style={{ maxWidth: '16rem' }}>
        {titleOf(state, deal)}
      </span>
    </button>
  )
}

function GoTo({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="meta inline-flex items-center gap-1 hover:underline"
    >
      {label} <Icon name="ArrowRight" size={12} />
    </button>
  )
}

/** Value by stage. A bar chart earns its place here because the shape of the
 *  pipeline is the question, not the individual numbers. */
function StageBars() {
  const { state, config, version } = useStore()
  const navigate = useNavigate()

  const rows = useMemo(() => {
    const deals = entitiesOfKind(state, 'deal')
    const value = (d: EntityRecord) => Number(d.fields.value) || 0
    return config.stages.map((stage) => {
      const items = deals.filter((d) => stageOf(d, config.stages) === stage.key)
      return {
        stage,
        count: items.length,
        value: items.reduce((sum, d) => sum + value(d), 0),
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, config])

  const max = Math.max(1, ...rows.map((r) => r.value))
  const hue = (kind: string) =>
    kind === 'won' || kind === 'delivery'
      ? 'var(--good)'
      : kind === 'closed'
        ? 'var(--marine)'
        : kind === 'lost'
          ? 'var(--crit)'
          : 'var(--ember)'

  return (
    <div className="space-y-2 py-1">
      {rows.map(({ stage, count, value }) => (
        <button
          key={stage.key}
          type="button"
          onClick={() => navigate('/deals')}
          className="text-body flex w-full items-center gap-3 text-left"
        >
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: hue(stage.kind) }}
          />
          <span className="w-24 shrink-0 truncate">{stage.label}</span>
          {/* The bar and its figures travel TOGETHER.
              The bar used to run the full column with the count and the amount
              pinned at the far right edge, five hundred pixels from the end of the
              mark they describe — so reading one stage meant crossing the page and
              reading one row against another meant crossing it twice. The track is
              capped at the largest value and the numbers sit at the bar's own end,
              which is where the eye already is. */}
          <span className="flex min-w-0 flex-1 items-center gap-2.5">
            {/* Shrinkable, not `shrink-0`: the track is a percentage of the row and
                the figures sit after it, so at the widest stage the two together
                would otherwise run past the column's right edge. */}
            <span
              className="bar"
              style={{ width: `${Math.max(4, Math.round((value / max) * 100))}%`, minWidth: 4 }}
            >
              <i
                style={{
                  width: '100%',
                  background: hue(stage.kind),
                  opacity: 0.6,
                }}
              />
            </span>
            <span
              className="num shrink-0"
              style={{ fontSize: 'var(--text-meta)', color: 'var(--fg)' }}
            >
              {money(value, '·')}
            </span>
            <span
              className="num shrink-0"
              style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
            >
              {count}
            </span>
          </span>
        </button>
      ))}
    </div>
  )
}
